import { FunctionComponent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { TicketLinkedEntityType } from "@signalist/shared-types";

import { ReportProblemButton } from "@/components/SupportTicket/ReportProblemButton";

import { useAppSelector } from "@/redux/hooks";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useGetZohoOAuthStatusQuery } from "@/pages/Private/redux/zoho/zoho.api";
import { useGetPipedriveOAuthStatusQuery } from "@/pages/Private/redux/pipedrive/pipedrive.api";
import { useGetDynamicsOAuthStatusQuery } from "@/pages/Private/redux/dynamics/dynamics.api";
import { useGetSalesforceOAuthStatusQuery } from "@/pages/Private/redux/salesforce/salesforce.api";
import { CrmProvider } from "@/pages/Private/redux/crm/crm.schema";
import { AddToMenu } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/AddToMenu";
import { useLeadPanelActions } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/useLeadPanelActions";
import { deriveLeadMembership } from "@/pages/Private/pages/Contacts/components/LeadPanelActions/leadPanelActions.utils";
import { useConnectedCrmProviders } from "@/pages/Private/pages/Integrations/hooks/useConnectedCrmProviders";
import { toAddToCrmListOption } from "@/pages/Private/components/AddToCrm/addToCrmListOptions";
import { useAddToCrmModal } from "@/pages/Private/components/AddToCrm/useAddToCrmModal";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { ListPerson } from "../../../List/schema/list";
import { InboxCrmDealModals } from "./InboxCrmDealModals";

interface InboxActionBarProps {
	/** The lead whose thread is open; the bar renders empty without one. */
	listPerson?: ListPerson;
	/** The batch "Send N messages" / clear-drafts buttons from `useAddInboxChat`. */
	renderSendButtons: () => ReactNode;
}

/**
 * The sticky footer under the Inbox thread (mockup §5 "Action bar"): a right-aligned cluster with
 * the unified "Add" menu (add to list / Profile Tracker / webhook / CRM contact, plus "Create
 * deal" opening the provider's deal modal) next to the batch send buttons. Reuses the lead-panel
 * {@link AddToMenu} with the campaign row hidden and the menu opening upward, right-anchored to
 * the trigger with left-aligned option rows.
 */
export const InboxActionBar: FunctionComponent<InboxActionBarProps> = ({
	listPerson,
	renderSendButtons,
}) => {
	const { t } = useTranslation();
	const { user } = useAppSelector(authSelector);
	const [dealModalProvider, setDealModalProvider] = useState<CrmProvider | null>(null);

	const prospect = listPerson?.prospect;

	const actions = useLeadPanelActions({
		prospectId: prospect?.id ?? 0,
		prospectName: prospect?.name ?? "",
		listPersonId: listPerson?.id,
	});
	const connectedCrmProviders = useConnectedCrmProviders();
	const crmModal = useAddToCrmModal({
		selectedCount: 1,
		isLoading: actions.isAddingToCrm,
		currentList: toAddToCrmListOption(listPerson),
		onSubmit: actions.addToCrm,
	});

	const isHubspotActive = useIsFeatureActive(FeaturesCodes.HUBSPOT);
	const isZohoActive = useIsFeatureActive(FeaturesCodes.ZOHO_CRM);
	const isPipedriveActive = useIsFeatureActive(FeaturesCodes.PIPEDRIVE_CRM);
	const isDynamicsActive = useIsFeatureActive(FeaturesCodes.MSDYNAMICS_CRM);
	const isSalesforceActive = useIsFeatureActive(FeaturesCodes.SALESFORCE_CRM);
	const { data: zohoOAuthStatus } = useGetZohoOAuthStatusQuery(undefined, {
		skip: !isZohoActive,
	});
	const { data: pipedriveOAuthStatus } = useGetPipedriveOAuthStatusQuery(undefined, {
		skip: !isPipedriveActive,
	});
	const { data: dynamicsOAuthStatus } = useGetDynamicsOAuthStatusQuery(undefined, {
		skip: !isDynamicsActive,
	});
	const { data: salesforceOAuthStatus } = useGetSalesforceOAuthStatusQuery(undefined, {
		skip: !isSalesforceActive,
	});

	const dealProviders = [
		isHubspotActive && user?.company?.hasHubspotApiKey && CrmProvider.HUBSPOT,
		isZohoActive && (zohoOAuthStatus?.connected ?? false) && CrmProvider.ZOHO,
		isPipedriveActive && (pipedriveOAuthStatus?.connected ?? false) && CrmProvider.PIPEDRIVE,
		isDynamicsActive && (dynamicsOAuthStatus?.connected ?? false) && CrmProvider.MSDYNAMICS,
		isSalesforceActive && (salesforceOAuthStatus?.connected ?? false) && CrmProvider.SALESFORCE,
	].filter((provider): provider is CrmProvider => Boolean(provider));

	const membership = deriveLeadMembership({
		listPersons: listPerson ? [{ id: listPerson.id }] : null,
		trackerPersons: prospect?.trackerPersons,
		crmProspects: prospect?.crmProspects,
	});

	return (
		<div className="shrink-0 h-12 flex flex-row justify-end items-center border-t border-t-border px-3 py-2 bg-white w-full">
			{listPerson && (
				<div className="flex flex-row items-center gap-x-2">
					{/* PREC-3938 decision 5: the Inbox thread has no id of its own; the ListPerson is the conversation. */}
					<ReportProblemButton
						prefill={{
							subject: t("support.reportProblemSubject.conversation", {
								name: prospect?.name ?? "",
							}),
							linkedEntityLabel: prospect?.name ?? undefined,
							linkedEntity: { type: TicketLinkedEntityType.CONVERSATION, id: listPerson.id },
						}}
					/>
					{prospect?.id && (
						<AddToMenu
							actions={actions}
							className="w-fit"
							connectedCrmProviders={connectedCrmProviders}
							dealProviders={dealProviders}
							listPerson={listPerson}
							membership={membership}
							placement="top"
							showCampaign={false}
							triggerLabel={t("inbox.actionBar.add")}
							onAddToCrm={crmModal.openAddToCrm}
							onCreateDeal={(provider) => setDealModalProvider(provider)}
						/>
					)}
					{renderSendButtons()}
				</div>
			)}
			{listPerson && dealModalProvider && (
				<InboxCrmDealModals
					listPerson={listPerson}
					showDynamics={dealModalProvider === CrmProvider.MSDYNAMICS}
					showSalesforce={dealModalProvider === CrmProvider.SALESFORCE}
					showHubspot={dealModalProvider === CrmProvider.HUBSPOT}
					showPipedrive={dealModalProvider === CrmProvider.PIPEDRIVE}
					showZoho={dealModalProvider === CrmProvider.ZOHO}
					onCloseDynamics={() => setDealModalProvider(null)}
					onCloseSalesforce={() => setDealModalProvider(null)}
					onCloseHubspot={() => setDealModalProvider(null)}
					onClosePipedrive={() => setDealModalProvider(null)}
					onCloseZoho={() => setDealModalProvider(null)}
				/>
			)}
			{crmModal.renderAddToCrmModal()}
			{actions.renderSnackbar()}
		</div>
	);
};
